import Bugsnag from "@bugsnag/js";
import { App } from "@capacitor/app";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { holdAppleAnswer } from "../login/apple";
import { onSessionEnd, onSessionStart } from "../session.svelte";
import { errorLog } from "../utils/logger";
import { listenForPushRegistration, refreshPushRegistration } from "./push";
import { initFontSizeZoom } from "./text-zoom";

// Every Capacitor listener, in one place: the React app's index.tsx start-up, DeepLinkListener,
// NativeStatusBar and TextZoomHandler. The app's listeners last as long as the app; the push
// registration's, as long as the session.

/**
 * Start the apps' listeners: links that open the app go to `navigate`, and the text size is set
 * again whenever the app comes back. Style the status bar, and while signed in, give the API the
 * device's push token. Returns what stops them all.
 */
export async function initNative(navigate: (url: string) => unknown): Promise<() => Promise<void>> {
  if (!Capacitor.isNativePlatform()) {
    return () => Promise.resolve();
  }

  try {
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: Style.Dark });
    // The webview goes under the status bar where it can, and the pages leave room for it with
    // env(safe-area-inset-top): the status bar shows their color, on iOS at least.
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch (err) {
    report(err, "Could not style the status bar");
  }

  const handles: PluginListenerHandle[] = [
    // Deep links, and Android's answer from Sign in with Apple, which is in the fragment. The
    // fragment stays out of the URL, where Bugsnag would record it; the login page gets it from
    // holdAppleAnswer.
    await App.addListener("appUrlOpen", ({ url }) => {
      const link = new URL(url);
      holdAppleAnswer(link);
      if (link.pathname) {
        void navigate(link.pathname + link.search);
      }
    }),
    // The text size may have changed in the device's settings meanwhile.
    await App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        initFontSizeZoom().catch((err: unknown) => report(err, "Could not set the text size"));
      }
    }),
  ];

  // Once per session. (Signing in over a session ends it first, so the new one starts its own.)
  let stopListening: (() => Promise<void>) | undefined;
  async function stopPushListeners() {
    const stop = stopListening;
    stopListening = undefined;
    await stop?.();
  }
  const stopStarting = onSessionStart(async (how) => {
    if (!stopListening) {
      stopListening = await listenForPushRegistration();
      onSessionEnd(stopPushListeners);
    }
    // Signing in registers the device when it asks for permission.
    if (how === "opened") {
      await refreshPushRegistration();
    }
  });

  return async () => {
    stopStarting();
    await Promise.all([stopPushListeners(), ...handles.map((handle) => handle.remove())]);
  };
}

function report(err: unknown, message: string): void {
  errorLog(err, message);
  Bugsnag.notify(err instanceof Error ? err : new Error(String(err)));
}
